import { isNil } from 'lodash';
import { MutableRefObject, useMemo } from 'react';
import Collapsible from 'react-collapsible';
import { ExecutionInfo } from '../../hook/live-execution.hooks';
import {
  FlowItem,
  FlowItemSharedParameterRef,
  FlowItemText,
  FlowSection,
  FlowState,
  FlowValidateError,
  SystemUnit,
} from '../../model/flow.types';
import {
  instanceOfFlowItemSectionParent,
  instanceOfPlaceholder,
} from '../../util/flow-item.util';
import { getParameterValue } from '../../util/flow-parameter.util';
import { FlowItemAction } from '../flow-item/view/flow-item-base';
import { FlowItemSection } from '../flow-item/view/flow-item-section';
import { FlowItemRow } from './flow-item-row';
import { SaveFlowFn } from './flow-ref';

export interface FlowSectionComponentProps {
  section: FlowSection;
  flowItemTextArr: (FlowItemText | undefined)[];
  flowItemClasses?: string[];
  canAddReorderItem: boolean;
  flowValidationErrors?: FlowValidateError[];
  getActions: (index: number) => FlowItemAction[] | undefined;
  onAddItemsAfter: (index?: number) => Promise<void>;
  flowState: FlowState;
  selectedItemIds: string[];
  onSelectItem: (id: string) => string[];
  shouldShowExecutionStatus: boolean;
  executionInfo?: ExecutionInfo;
  startItemIndex: number;
  getFlowItemsRef: MutableRefObject<() => FlowItem[]>;
  saveFlow: SaveFlowFn;
  updateFlowStartAt: (
    id: string,
    longPress: boolean,
    withStepMode?: boolean
  ) => Promise<void>;
  onLongPress: (e: Event, id: string) => Promise<void>;
  presentItemQuickActions?: (e: Event, id: string) => void;
  sectionCollapsedState: Record<string, boolean>;
  toggleCollapsed: (itemId: string, collapsed: boolean) => void;
  system: SystemUnit;
}

const FlowSectionComponent = (props: FlowSectionComponentProps) => {
  const {
    section,
    flowItemTextArr,
    flowValidationErrors,
    getActions,
    canAddReorderItem,
    onAddItemsAfter,
    onSelectItem,
    flowState,
    selectedItemIds,
    shouldShowExecutionStatus,
    executionInfo,
    startItemIndex,
    onLongPress,
    presentItemQuickActions,
    sectionCollapsedState,
    toggleCollapsed,
    flowItemClasses,
    system,
  } = props;

  const sectionItem = section?.section?.item;
  const isStitchSection =
    !isNil(sectionItem) && instanceOfFlowItemSectionParent(sectionItem);

  const color = useMemo(() => {
    const item = sectionItem;
    if (item) {
      return getParameterValue(item, FlowItemSharedParameterRef.SECTION_COLOR);
    }
  }, [sectionItem]);

  const borderLeftColor = color ? `${color}` : `transparent`;

  const isSectionItemSelected =
    section.section?.item?.id !== undefined
      ? selectedItemIds.includes(section.section?.item?.id)
      : false;

  const isSectionItemExecuting = executionInfo?.index
    ? section.items.map((s) => s.index).includes(executionInfo?.index)
    : false;

  const sectionItemId = section.section?.item?.id ?? '';
  const collapsed = sectionCollapsedState[sectionItemId] ?? false;

  const sectionItemComponents = section.items.map((sectionItem, index) => {
    const label = instanceOfPlaceholder(sectionItem.item)
      ? undefined
      : flowItemTextArr[sectionItem.index] ?? undefined;

    const isFirstItemWithinSection = section.section?.item
      ? index === 0
      : false;

    const classes = ['flow-item-container'];

    if (!isNil(section.section?.parentItem)) {
      classes.push('nested');
    }

    return (
      <div
        key={sectionItem.index}
        className={classes.join(' ')}
        style={{
          borderLeftColor,
        }}
      >
        <FlowItemRow
          index={sectionItem.index}
          flowItem={sectionItem.item}
          flowValidationErrors={flowValidationErrors}
          canAddReorderItem={canAddReorderItem}
          getActions={getActions}
          onAddItemsAfter={onAddItemsAfter}
          onSelectItem={
            flowState == FlowState.DEFAULT ? onSelectItem : undefined
          }
          selectedItems={selectedItemIds}
          isSectionSelected={isSectionItemSelected}
          executionInfo={
            shouldShowExecutionStatus &&
            executionInfo?.index === sectionItem.index
              ? executionInfo
              : undefined
          }
          isStartItem={startItemIndex === sectionItem.index}
          isInactive={startItemIndex > sectionItem.index}
          onLongPress={onLongPress}
          label={label?.label}
          presentItemQuickActions={
            flowState == FlowState.DEFAULT && !isStitchSection
              ? presentItemQuickActions
              : undefined
          }
          classes={flowItemClasses}
          addPrecedingStem={isFirstItemWithinSection}
          system={system}
        />
      </div>
    );
  });

  const sectionItems = useMemo(() => {
    if (canAddReorderItem) {
      return collapsed ? null : sectionItemComponents;
    } else {
      const transitionTime = Math.min(
        150 + sectionItemComponents.length * 50,
        400
      );
      return (
        <Collapsible
          trigger={<></>}
          open={!collapsed}
          easing={'ease-in-out'}
          transitionTime={transitionTime}
        >
          {sectionItemComponents}
        </Collapsible>
      );
    }
  }, [canAddReorderItem, collapsed, sectionItemComponents]);

  const sectionLabel =
    section?.section?.index !== undefined
      ? flowItemTextArr[section.section.index]?.label
      : undefined;

  const ComponentToRender = (
    <>
      {section.section && (
        <FlowItemSection
          item={section.section.item}
          itemIndex={section.section.index}
          label={sectionLabel}
          canAddReorderItem={canAddReorderItem}
          enableCollapse={flowState === FlowState.DEFAULT}
          collapsed={collapsed}
          onSelectItem={
            flowState == FlowState.DEFAULT ? onSelectItem : undefined
          }
          toggleCollapsed={(collapsed) => {
            toggleCollapsed(sectionItemId, collapsed);
          }}
          parentItem={section.section.parentItem}
          isItemSelected={isSectionItemSelected}
          classes={flowItemClasses}
          isSectionItemExecuting={isSectionItemExecuting}
          executionInfo={shouldShowExecutionStatus ? executionInfo : undefined}
        />
      )}
      {sectionItems}
    </>
  );

  // if we are not reordering then we will wrap the component in an additional div allowing
  // the sticky header to be pushed offscreen vs overlapped
  return (
    <>
      {canAddReorderItem ? ComponentToRender : <div>{ComponentToRender}</div>}
    </>
  );
};

export { FlowSectionComponent };
